/**
 * TutorialLayer — everything the tutorial draws, portalled out of the app tree.
 *
 * The portal matters: the editor nests the canvas and both sidebars inside
 * elements that create stacking contexts, so an overlay rendered in place would
 * be clipped by whichever panel happened to contain it.
 */

import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import AuthContext from '../../context/AuthContext';
import { useTutorial } from '../engine/TutorialContext';
import TutorialOverlay from './TutorialOverlay';
import TutorialTooltip from './TutorialTooltip';
import TutorialPausedPill from './TutorialPausedPill';
import TutorialCompleteModal from './TutorialCompleteModal';

const TutorialLayer = () => {
    const {
        isActive,
        isPaused,
        isFinished,
        tutorial,
        step,
        stepIndex,
        stepCount,
        hint,
        rects,
        anchorEl,
        resolveError,
        next,
        exit,
        retry,
        resume,
        dismissComplete,
        ctx,
    } = useTutorial();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);

    if (typeof document === 'undefined') return null;

    if (isFinished && tutorial?.content?.complete) {
        // The scenario the tutorial just published is on the author's own
        // profile, so that is where "see my scenarios" goes — but only when we
        // know the username the route needs.
        const profilePath = user?.username ? `/user/${user.username}` : null;

        return createPortal(
            <TutorialCompleteModal
                content={tutorial.content.complete}
                onClose={dismissComplete}
                onViewScenarios={
                    profilePath
                        ? () => {
                              dismissComplete();
                              navigate(profilePath);
                          }
                        : null
                }
            />,
            document.body
        );
    }

    if (isPaused) {
        return createPortal(<TutorialPausedPill onResume={resume} onExit={exit} />, document.body);
    }

    if (!isActive || !step) return null;

    const body = typeof step.body === 'function' ? step.body(ctx) : step.body;

    return createPortal(
        <>
            <TutorialOverlay rects={rects} />
            <TutorialTooltip
                step={step}
                stepIndex={stepIndex}
                stepCount={stepCount}
                hint={hint}
                body={body}
                anchorEl={anchorEl}
                resolveError={resolveError}
                onNext={next}
                onExit={exit}
                onRetry={retry}
            />
        </>,
        document.body
    );
};

export default TutorialLayer;
